import React, { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { MessageSquare, User, Lock, Unlock } from 'lucide-react'
import { supportApi } from '../../lib/api'
import DataTable, { Column } from '../../components/shared/DataTable'
import FilterBar from '../../components/shared/FilterBar'
import DetailDrawer from '../../components/shared/DetailDrawer'
import StatusBadge from '../../components/shared/StatusBadge'
import Pagination from '../../components/shared/Pagination'
import Timeline from '../../components/shared/Timeline'
import { useToast } from '../../hooks/useToast'
import { usePermission } from '../../hooks/usePermission'
import { formatDate, formatDatetime } from '../../lib/utils'
import type { SupportTicket, TicketMessage } from '../../types'
import { useAuth } from '../../hooks/useAuth'

import { tx } from '../../lib/i18n'
const statusOptions = [
  { label: tx('Open'), value: 'open' },
  { label: tx('Pending'), value: 'pending' },
  { label: tx('Resolved'), value: 'resolved' },
  { label: tx('Closed'), value: 'closed' },
]

const priorityOptions = [
  { label: tx('Low'), value: 'low' },
  { label: tx('Medium'), value: 'medium' },
  { label: tx('High'), value: 'high' },
  { label: tx('Urgent'), value: 'urgent' },
]

const priorityColor: Record<string, string> = {
  low: 'bg-gray-100 text-gray-600',
  medium: 'bg-yellow-100 text-yellow-700',
  high: 'bg-orange-100 text-orange-700',
  urgent: 'bg-red-100 text-red-700',
}

const SupportPage: React.FC = () => {
  const queryClient = useQueryClient()
  const { showToast } = useToast()
  const { currentAdmin } = useAuth()
  const { can } = usePermission()

  const [page, setPage] = useState(1)
  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState('')
  const [priorityFilter, setPriorityFilter] = useState('')

  const [pickedTicket, setSelectedTicket] = useState<SupportTicket | null>(null)
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [replyBody, setReplyBody] = useState('')
  const [isInternal, setIsInternal] = useState(false)

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['tickets', { page, search, statusFilter, priorityFilter }],
    queryFn: () =>
      supportApi.listTickets({ page, limit: 20, search, status: statusFilter || undefined, priority: priorityFilter || undefined })
        .then((r) => r.data),
  })

  const tickets = data?.data ?? []
  // follow the live list so status, assignee and new messages show right after a change
  const selectedTicket = (pickedTicket && (tickets as SupportTicket[]).find((t) => t.id === pickedTicket.id)) ?? pickedTicket
  const total = data?.total ?? 0
  const totalPages = data?.totalPages ?? 1

  const replyMutation = useMutation({
    mutationFn: () => supportApi.replyTicket(selectedTicket!.id, replyBody, isInternal),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tickets'] })
      showToast({ type: 'success', message: tx('Reply sent.') })
      setReplyBody('')
    },
    onError: () => showToast({ type: 'error', message: tx('Failed to send reply.') }),
  })

  const updateMutation = useMutation({
    mutationFn: (data: Partial<SupportTicket>) => supportApi.updateTicket(selectedTicket!.id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tickets'] })
      showToast({ type: 'success', message: tx('Ticket updated.') })
    },
    onError: () => showToast({ type: 'error', message: tx('Failed to update ticket.') }),
  })

  const assignMutation = useMutation({
    mutationFn: () => supportApi.assignTicket(selectedTicket!.id, currentAdmin!.id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tickets'] })
      showToast({ type: 'success', message: tx('Ticket assigned to you.') })
    },
  })

  const closeMutation = useMutation({
    mutationFn: () => supportApi.closeTicket(selectedTicket!.id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tickets'] })
      showToast({ type: 'success', message: tx('Ticket closed.') })
      setDrawerOpen(false)
    },
  })

  const columns: Column<SupportTicket>[] = [
    {
      key: 'id', header: tx('Ticket #'),
      render: (r) => <span className="font-mono text-xs text-gray-600">#{r.id.replace('ticket-', '')}</span>,
    },
    {
      key: 'shopName', header: tx('Shop'),
      render: (r) => <span className="text-gray-700 text-sm">{r.shopName ?? <em className="text-gray-400">{tx('No shop')}</em>}</span>,
    },
    {
      key: 'subject', header: tx('Subject'),
      render: (r) => <span className="font-medium text-gray-900 max-w-xs block truncate">{r.subject}</span>,
    },
    {
      key: 'priority', header: tx('Priority'),
      render: (r) => <span className={`text-xs font-medium px-2 py-0.5 rounded-full capitalize ${priorityColor[r.priority]}`}>{r.priority}</span>,
    },
    { key: 'status', header: tx('Status'), render: (r) => <StatusBadge status={r.status} /> },
    {
      key: 'assignedAdminName', header: tx('Assigned'),
      render: (r) => <span className="text-gray-500 text-sm">{r.assignedAdminName ?? <em className="text-gray-300">{tx('Unassigned')}</em>}</span>,
    },
    {
      key: 'createdAt', header: tx('Created'),
      render: (r) => <span className="text-gray-400 text-sm">{formatDate(r.createdAt)}</span>,
    },
  ]

  const timelineItems = selectedTicket?.messages?.map((msg) => ({
    id: msg.id,
    title: msg.isInternal ? `[Internal] ${msg.authorName ?? 'Admin'}` : msg.authorName ?? 'Unknown',
    description: msg.body,
    timestamp: msg.createdAt,
    color: msg.isInternal
      ? 'bg-amber-100 text-amber-700'
      : msg.authorAdminId
        ? 'bg-indigo-100 text-indigo-600'
        : 'bg-gray-100 text-gray-600',
  })) ?? []

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-xl font-bold text-gray-900">{tx('Support')}</h1>
        <p className="text-sm text-gray-500 mt-0.5">{tx('Manage support tickets and customer communications')}</p>
      </div>

      <FilterBar
        search={{ value: search, onChange: setSearch, placeholder: tx('Search by subject, shop...') }}
        filters={[
          { key: 'status', label: tx('Status'), options: statusOptions, value: statusFilter, onChange: setStatusFilter },
          { key: 'priority', label: tx('Priority'), options: priorityOptions, value: priorityFilter, onChange: setPriorityFilter },
        ]}
        onReset={() => { setSearch(''); setStatusFilter(''); setPriorityFilter(''); setPage(1) }}
      />

      <div>
        <DataTable
          columns={columns}
          data={tickets}
          isLoading={isLoading}
          emptyMessage={tx('No tickets found.')}
          onRowClick={(row) => { setSelectedTicket(row); setDrawerOpen(true) }}
        />
        <Pagination page={page} totalPages={totalPages} total={total} limit={20} onPageChange={setPage} />
      </div>

      <DetailDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        title={selectedTicket?.subject ?? 'Ticket Detail'}
        subtitle={selectedTicket ? `${selectedTicket.shopName ?? 'No shop'} · Created ${formatDate(selectedTicket.createdAt)}` : undefined}
        width="lg"
      >
        {selectedTicket && (
          <div className="space-y-5">
            {/* Header info */}
            <div className="grid grid-cols-2 gap-3">
              <div className="bg-gray-50 rounded-lg p-3">
                <p className="text-xs text-gray-400">{tx('Status')}</p>
                <div className="mt-1">
                  <select
                    value={selectedTicket.status}
                    onChange={(e) => updateMutation.mutate({ status: e.target.value as SupportTicket['status'] })}
                    disabled={!can('support.update')}
                    className="text-sm border border-gray-200 rounded-lg px-2 py-1 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  >
                    {statusOptions.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                </div>
              </div>
              <div className="bg-gray-50 rounded-lg p-3">
                <p className="text-xs text-gray-400">{tx('Priority')}</p>
                <div className="mt-1">
                  <select
                    value={selectedTicket.priority}
                    onChange={(e) => updateMutation.mutate({ priority: e.target.value as SupportTicket['priority'] })}
                    disabled={!can('support.update')}
                    className="text-sm border border-gray-200 rounded-lg px-2 py-1 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  >
                    {priorityOptions.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                </div>
              </div>
              <div className="bg-gray-50 rounded-lg p-3">
                <p className="text-xs text-gray-400">{tx('Assigned To')}</p>
                <p className="mt-1 text-sm font-medium text-gray-800">{selectedTicket.assignedAdminName ?? tx('Unassigned')}</p>
              </div>
              <div className="bg-gray-50 rounded-lg p-3">
                <p className="text-xs text-gray-400">{tx('Tags')}</p>
                <div className="mt-1 flex flex-wrap gap-1">
                  {selectedTicket.tags.map((tag) => (
                    <span key={tag} className="text-xs bg-indigo-100 text-indigo-700 px-2 py-0.5 rounded-full">{tag}</span>
                  ))}
                </div>
              </div>
            </div>

            {/* Description */}
            <div className="bg-blue-50 border border-blue-100 rounded-lg p-4">
              <p className="text-xs font-medium text-blue-700 mb-1">{tx('Original Message')}</p>
              <p className="text-sm text-blue-800">{selectedTicket.description}</p>
            </div>

            {/* Actions */}
            {can('support.update') && (
            <div className="flex gap-2 flex-wrap">
              <button
                onClick={() => assignMutation.mutate()}
                disabled={assignMutation.isPending}
                className="flex items-center gap-1.5 px-3 py-1.5 text-sm border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
              >
                <User className="h-4 w-4" /> {tx('Assign to Me')}
              </button>
              <button
                onClick={() => closeMutation.mutate()}
                disabled={closeMutation.isPending}
                className="flex items-center gap-1.5 px-3 py-1.5 text-sm border border-red-200 text-red-600 rounded-lg hover:bg-red-50 transition-colors"
              >
                <Lock className="h-4 w-4" /> {tx('Close Ticket')}
              </button>
            </div>
            )}

            {/* Messages timeline */}
            <div>
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">
                {tx('Messages (')}{selectedTicket.messages?.length ?? 0})
              </p>
              <Timeline items={timelineItems} />
            </div>

            {/* Reply box */}
            {can('support.reply') && (
            <div className="border border-gray-200 rounded-xl p-4 space-y-3">
              <div className="flex items-center gap-3">
                <p className="text-sm font-medium text-gray-700">{tx('Reply')}</p>
                <button
                  onClick={() => setIsInternal((v) => !v)}
                  className={`flex items-center gap-1.5 px-2.5 py-1 text-xs rounded-lg border transition-colors ${isInternal ? 'bg-amber-100 text-amber-700 border-amber-200' : 'border-gray-200 text-gray-500 hover:bg-gray-50'}`}
                >
                  {isInternal ? <Lock className="h-3 w-3" /> : <Unlock className="h-3 w-3" />}
                  {isInternal ? tx('Internal Note') : 'Public Reply'}
                </button>
              </div>
              <textarea
                rows={3}
                value={replyBody}
                onChange={(e) => setReplyBody(e.target.value)}
                placeholder={isInternal ? 'Internal note visible to admins only...' : 'Reply to the customer...'}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 resize-none"
              />
              <button
                onClick={() => replyMutation.mutate()}
                disabled={!replyBody.trim() || replyMutation.isPending}
                className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700 disabled:opacity-50 transition-colors"
              >
                <MessageSquare className="h-4 w-4" />
                {replyMutation.isPending ? tx('Sending...') : 'Send Reply'}
              </button>
            </div>
            )}
          </div>
        )}
      </DetailDrawer>
    </div>
  )
}

export default SupportPage
